import { FormEvent, useEffect, useState } from 'react';

const GREEN = '#0F2A1D';
const CREAM = '#F5F2EC';
const INK = '#1A1A1A';
const RULE = 'rgba(26,26,26,0.14)';

interface Props {
  open: boolean;
  onClose: () => void;
}

export function QuotePanel({ open, onClose }: Props) {
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitted(true);
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Get a quote"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,42,29,0.45)',
        zIndex: 100,
        display: 'flex',
        justifyContent: 'flex-end',
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(480px, 100%)',
          background: CREAM,
          height: '100%',
          overflowY: 'auto',
          padding: '32px 32px 40px',
          boxShadow: '-12px 0 48px rgba(0,0,0,0.18)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 11,
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              color: GREEN,
            }}
          >
            Quote request
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              background: 'transparent',
              border: 0,
              fontSize: 22,
              cursor: 'pointer',
              color: INK,
              lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>
        <h2
          style={{
            fontFamily: 'var(--font-display)',
            fontSize: 36,
            fontWeight: 500,
            letterSpacing: '-0.02em',
            margin: '12px 0 8px',
            color: GREEN,
          }}
        >
          Send a load
        </h2>
        <p style={{ fontSize: 14, color: 'rgba(26,26,26,0.72)', marginTop: 0 }}>
          Average reply in under 90 seconds with a firm rate.
        </p>
        {submitted ? (
          <div
            style={{
              marginTop: 24,
              padding: '20px 22px',
              background: 'rgba(15,42,29,0.06)',
              border: `1px solid ${RULE}`,
            }}
          >
            <strong style={{ display: 'block', marginBottom: 6 }}>Thanks — dispatch has it.</strong>
            <span style={{ fontSize: 13, color: 'rgba(26,26,26,0.72)' }}>
              We&rsquo;ll reach out on the contact you provided.
            </span>
          </div>
        ) : (
          <form onSubmit={handleSubmit} style={{ marginTop: 20, display: 'grid', gap: 14 }}>
            <Field label="Origin" name="origin" placeholder="City, ST" required />
            <Field label="Destination" name="destination" placeholder="City, ST" required />
            <Field label="Pickup window" name="pickup" placeholder="Mon AM" required />
            <Field label="Equipment" name="equipment" placeholder="Hotshot / flatbed / stepdeck" required />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <Field label="Weight (lb)" name="weight" type="number" required />
              <Field label="Commodity" name="commodity" placeholder="Optional" />
            </div>
            <Field label="Contact email" name="email" type="email" required />
            <button
              type="submit"
              style={{
                marginTop: 8,
                background: INK,
                color: CREAM,
                border: 0,
                padding: '14px 22px',
                fontSize: 14,
                fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              Send to dispatch →
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

function Field({
  label,
  name,
  type = 'text',
  placeholder,
  required,
}: {
  label: string;
  name: string;
  type?: string;
  placeholder?: string;
  required?: boolean;
}) {
  return (
    <label style={{ display: 'block' }}>
      <div
        style={{
          fontFamily: 'var(--font-mono)',
          fontSize: 10,
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          color: 'rgba(26,26,26,0.55)',
          marginBottom: 4,
        }}
      >
        {label}
        {required && ' *'}
      </div>
      <input
        name={name}
        type={type}
        placeholder={placeholder}
        required={required}
        style={{
          width: '100%',
          padding: '10px 12px',
          border: `1px solid ${RULE}`,
          background: CREAM,
          fontSize: 14,
          fontFamily: 'inherit',
          color: INK,
          outline: 'none',
        }}
        onFocus={(e) => (e.currentTarget.style.borderColor = INK)}
        onBlur={(e) => (e.currentTarget.style.borderColor = RULE)}
      />
    </label>
  );
}
